"use client";

import { Settings } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface QualityLevel {
  height: number;
  bitrate: number;
  index: number;
}

interface VideoPlayerSettingsProps {
  playbackRate: number;
  onPlaybackRateChange: (rate: number) => void;
  qualityLevels: QualityLevel[];
  currentQuality: number;
  onQualityChange: (index: number) => void;
  className?: string;
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

export function VideoPlayerSettings({
  playbackRate,
  onPlaybackRateChange,
  qualityLevels,
  currentQuality,
  onQualityChange,
  className,
}: VideoPlayerSettingsProps) {
  const getQualityLabel = (level: QualityLevel) => {
    if (level.height > 0) {
      if (level.height >= 1080) return "1080p";
      if (level.height >= 720) return "720p";
      if (level.height >= 480) return "480p";
      if (level.height >= 360) return "360p";
      return `${level.height}p`;
    }
    if (level.bitrate > 0) {
      const mbps = level.bitrate / 1_000_000;
      if (mbps >= 1) return `${mbps.toFixed(1)} Mbps`;
      return `${(level.bitrate / 1000).toFixed(0)} kbps`;
    }
    return `Nivel ${level.index + 1}`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "video-player-settings-btn flex items-center justify-center rounded-md p-2.5 text-white",
            className,
          )}
          aria-label="Configuración"
          suppressHydrationWarning
        >
          <Settings className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="top"
        sideOffset={8}
        className="w-48 border-white/10 bg-black/95 text-white backdrop-blur-sm"
      >
        <DropdownMenuLabel className="text-xs text-white/60">
          Velocidad
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={String(playbackRate)}
          onValueChange={(value) => onPlaybackRateChange(Number(value))}
        >
          {PLAYBACK_RATES.map((rate) => (
            <DropdownMenuRadioItem
              key={rate}
              value={String(rate)}
              className="data-[state=checked]:text-brand-red cursor-pointer text-sm focus:bg-white/10 focus:text-white"
            >
              {rate === 1 ? "Normal" : `${rate}x`}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        {qualityLevels.length > 0 && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuLabel className="text-xs text-white/60">
              Calidad
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={String(currentQuality)}
              onValueChange={(value) => onQualityChange(Number(value))}
            >
              <DropdownMenuRadioItem
                value="-1"
                className="data-[state=checked]:text-brand-red cursor-pointer text-sm focus:bg-white/10 focus:text-white"
              >
                Auto
              </DropdownMenuRadioItem>
              {qualityLevels
                .sort((a, b) => b.height - a.height)
                .map((level) => (
                  <DropdownMenuRadioItem
                    key={level.index}
                    value={String(level.index)}
                    className="data-[state=checked]:text-brand-red cursor-pointer text-sm focus:bg-white/10 focus:text-white"
                  >
                    {getQualityLabel(level)}
                  </DropdownMenuRadioItem>
                ))}
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
